import { ref, computed, onMounted, onUnmounted, inject, type ComputedRef } from 'vue'
import { useIntervalFn } from '@vueuse/core'
import { useI18n } from 'vue-i18n'
import useToast from './useToast'
import { useCoreApi } from './useCoreApi'
import type { PitcherInfo } from '@lib/types/app'
import type { User } from '@openapi'
import type { OrganizationSettings } from '@lib/types/organizationSettings.types'
import type { InstanceSettings } from '@lib/types/instanceSettings.types'
import type { LaunchDarklyEnv } from '@lib/types/launchDarkly.types'

// Singleton state
const state = {
  locks: ref<CanvasLock[]>([]),
  isChangingLock: ref(false),
  isFetching: ref(false),
  error: ref<Error | null>(null),
  fetchPromise: null as Promise<void> | null,
  isInitialized: false,
  activeInstances: 0,
  intervalInstance: null as ReturnType<typeof useIntervalFn> | null,
}

interface LockedBy {
  id: number
  name?: string
  email?: string
}

export interface CanvasLock {
  id: string
  canvas_id: string
  instance_id: string
  locked_by_id: number
  locked_by: LockedBy
  expires_at: string | null
}

export default function useCanvasLocks() {
  const { t } = useI18n()
  const { toast } = useToast()
  const { coreRestApiAxios } = useCoreApi()

  // Inject pitcherInfo to get instance ID if not provided in options
  const pitcherInfo = inject<ComputedRef<PitcherInfo>>(
    'pitcherInfo',
    computed(() => ({
      accessToken: '',
      instanceId: '',
      apiOrigin: '',
      isAnyTypeOfAdmin: false,
    })),
  )
  const myUser = inject<ComputedRef<User | null>>(
    'myUser',
    computed(() => null),
  )
  const pitcherSettings = inject<ComputedRef<OrganizationSettings & InstanceSettings>>(
    'pitcherSettings',
    computed(() => ({})),
  )
  const launchDarkly = inject<ComputedRef<LaunchDarklyEnv>>(
    'launchDarkly',
    computed(() => ({})),
  )

  const settingsInterval = pitcherSettings.value?.canvas?.locks_polling_interval
    ? parseInt(`${pitcherSettings.value?.canvas?.locks_polling_interval}`)
    : null
  const pollingInterval = settingsInterval && !isNaN(settingsInterval) ? settingsInterval : 30000 // Default to 30 seconds

  // Use provided instanceId or fallback to pitcherInfo
  const instanceId = computed(() => pitcherInfo?.value?.instanceId || '')

  const canvasLockMap = computed(() => {
    return state.locks.value.reduce(
      (acc, lock) => {
        acc[lock.canvas_id] = lock
        return acc
      },
      {} as Record<string, CanvasLock>,
    )
  })

  const isCanvasLocked = (canvasId: string) => {
    return !!canvasLockMap.value[canvasId]
  }

  const getCanvasLock = (canvasId: string) => {
    return canvasLockMap.value[canvasId] || null
  }

  const isLockedByCurrentUser = (canvasId: string) => {
    const lock = canvasLockMap.value[canvasId]
    return lock ? lock.locked_by_id === myUser.value?.id : false
  }

  function isLockedBySomeoneElse(canvasId: string): boolean {
    return !!getCanvasLock(canvasId) && !isLockedByCurrentUser(canvasId)
  }

  const getCanvasLockTooltip = (canvasId: string): string => {
    const lock = getCanvasLock(canvasId)
    if (!lock) return ''

    const isCurrentUser = isLockedByCurrentUser(canvasId)
    if (isCurrentUser) {
      return t('canvasUI.canvasLocks.lockedByYou')
    }

    return t('canvasUI.canvasLocks.lockedBy', { name: lock.locked_by.name })
  }

  const fetchLocks = async () => {
    if (!launchDarkly.value.enable_canvas_locks || !instanceId.value || !coreRestApiAxios?.value) return

    // If already fetching, return the existing promise
    if (state.fetchPromise) return state.fetchPromise

    try {
      state.isFetching.value = true
      state.error.value = null

      // Create a new promise for this fetch operation
      state.fetchPromise = (async () => {
        try {
          const response = await coreRestApiAxios.value!.get<any, CanvasLock[]>(
            `protected/canvas-locks/by-instance/${instanceId.value}`,
          )

          state.locks.value = response
        } catch (err) {
          state.error.value = err instanceof Error ? err : new Error(String(err))
          console.error('Error fetching canvas locks:', err)
        } finally {
          state.isFetching.value = false
          state.fetchPromise = null
        }
      })()

      return state.fetchPromise
    } catch (err) {
      console.error('Error initiating fetch:', err)
      state.fetchPromise = null
      throw err
    }
  }

  const lockCanvas = async (canvasId: string) => {
    if (!coreRestApiAxios?.value) {
      throw new Error('API client is not available')
    }

    // Check if canvas is already locked in our local state
    const existingLock = getCanvasLock(canvasId)
    if (existingLock) {
      const lockedByName = existingLock.locked_by?.name || t('canvasUI.canvasLocks.anotherUser')
      const errorMessage = t('canvasUI.canvasLocks.alreadyLockedBy', { name: lockedByName })
      toast.error(errorMessage)
      throw new Error(errorMessage)
    }

    try {
      state.isChangingLock.value = true
      state.error.value = null

      const response = await coreRestApiAxios.value.post<any, CanvasLock>('protected/canvas-locks', {
        canvas_id: canvasId,
        skip_error_toast: true, // Add skip_error_toast to the request data
      })

      // Add the new lock to the local state instead of refetching
      if (response) {
        state.locks.value = [...state.locks.value, response]
      }

      toast.success(t('canvasUI.canvasLocks.lockSuccess'))
      return response
    } catch (err: any) {
      state.error.value = err instanceof Error ? err : new Error(String(err))

      // Handle 409 Conflict response (canvas already locked)
      if (err.response?.status === 409) {
        const lock = err.response.data?.lock
        const lockedBy = lock?.locked_by
        const lockedByName = lockedBy?.name || t('canvasUI.canvasLocks.anotherUser')
        const errorMessage = t('canvasUI.canvasLocks.alreadyLockedBy', { name: lockedByName })
        toast.error(errorMessage)

        // Force refresh locks to get the latest state
        fetchLocks()
      } else {
        console.error('Error locking canvas:', err)
        toast.error(t('canvasUI.canvasLocks.lockError'))
      }

      throw err
    } finally {
      state.isChangingLock.value = false
    }
  }

  const unlockCanvas = async (lockId: string) => {
    if (!coreRestApiAxios?.value) {
      throw new Error('API client is not available')
    }

    try {
      state.isChangingLock.value = true
      state.error.value = null

      await coreRestApiAxios.value.delete<any, { success: boolean }>(`protected/canvas-locks/${lockId}`)

      // Remove the lock from local state instead of refetching
      state.locks.value = state.locks.value.filter((lock) => lock.id !== lockId)

      toast.success(t('canvasUI.canvasLocks.unlockSuccess'))
      return true
    } catch (err) {
      state.error.value = err instanceof Error ? err : new Error(String(err))
      console.error('Error unlocking canvas:', err)
      toast.error(t('canvasUI.canvasLocks.unlockError'))
      throw err
    } finally {
      state.isChangingLock.value = false
    }
  }

  // Set up polling for locks only once
  if (!state.intervalInstance) {
    state.intervalInstance = useIntervalFn(fetchLocks, pollingInterval)
  }

  onMounted(() => {
    // Increment active instances counter
    state.activeInstances++

    // Initialize only once
    if (!state.isInitialized) {
      fetchLocks()
      state.isInitialized = true
    }

    // Always resume the interval when a component using this composable is mounted
    state.intervalInstance?.resume()
  })

  onUnmounted(() => {
    // Decrement active instances counter
    state.activeInstances--

    // Only pause polling when no components are using the locks
    if (state.activeInstances <= 0) {
      state.intervalInstance?.pause()
      state.activeInstances = 0 // Safety reset
    }
  })

  return {
    locks: state.locks,
    isFetching: state.isFetching,
    isChangingLock: state.isChangingLock,
    isOrgAdmin: computed(() => !!pitcherInfo?.value?.isOrgAdmin),
    error: state.error,
    canvasLockMap,
    isCanvasLocked,
    getCanvasLock,
    isLockedByCurrentUser,
    getCanvasLockTooltip,
    fetchLocks,
    lockCanvas,
    unlockCanvas,
    isLockedBySomeoneElse,
  }
}
